import moment from "moment";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "shared/utils";
import { Badge, BadgeSize, BadgeVariant, Hide, Show } from "shared/components";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { ApplicationStatus, IncomeType } from "shared/types";
import { TablesSlice } from "../..";
import { Tabs } from "shared/components/Tabs";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { VerifiedReportedStats } from "./VerifiedReportedStats";
import { VerifiedHeader } from "../components/VerifiedHeader";
import { OfferLetterAssets } from "../components/OfferLetterAssets";
import { WarningRecommendation } from "../../components/WarningRecommendation";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { BankStatementItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/bank-statment";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import { Dispatch, SetStateAction } from "react";

export const VerifiedIncomeDetails = ({
  groupedIncomeItems,
  income,
  currentIncomeIndex,
  setCurrentIncomeIndex,
  currentItem,
  applicationId,
  applicationStatus,
  setShowReverifyIncomeModal,
  isManualApplicant,
  additionalData
}: {
  groupedIncomeItems: (VerifiedItem | BankStatementItem[])[];
  income: CombinedIncome;
  currentIncomeIndex: number;
  setCurrentIncomeIndex: Dispatch<SetStateAction<number>>;
  currentItem?: VerifiedItem | VerifiedItem[];
  applicationId?: string | number;
  applicationStatus: ApplicationStatus;
  setShowReverifyIncomeModal: Dispatch<SetStateAction<number | false>>;
  isManualApplicant: boolean;
  additionalData?: VerificationSpecificAdditionaData | null;
}) => {
  const tabsDisplayMap = groupedIncomeItems.reduce((acc, i, idx) => {
    const isLast = idx === groupedIncomeItems.length - 1,
      reverificationCount = groupedIncomeItems.length - idx - 1,
      isArchived = Array.isArray(i)
        ? i.every((item) => item.is_archived)
        : i.is_archived;

    acc[String(idx)] = (
      <p className="text-sm">
        {isLast
          ? "Original verification "
          : `Re-verification ${reverificationCount} `}
        <Badge
          size={BadgeSize.SM}
          className="ml-1"
          variant={isArchived ? BadgeVariant.BASE : BadgeVariant.SUCCESS}
        >
          {isArchived ? "Archived" : "Active"}
        </Badge>
      </p>
    );

    return acc;
  }, {});

  const currentItemReverifyLog = Array.isArray(currentItem)
    ? currentItem[0]?.reverify_log
    : currentItem?.reverify_log;

  return (
    <>
      {groupedIncomeItems.length > 1 ? (
        <>
          <Tabs
            variant="underline"
            tabs={groupedIncomeItems.map((_i, idx) => String(idx))}
            displayMap={tabsDisplayMap}
            setActiveTab={(t) => setCurrentIncomeIndex(Number(t))}
            selected={String(currentIncomeIndex)}
          />
          <OfferLetterAssets income={income} />
        </>
      ) : (
        <ContentTitle
          title={
            income.type === IncomeType.OfferLetter
              ? "Offer letter"
              : "Verification method"
          }
        >
          <Show if={income.type === IncomeType.OfferLetter}>
            <WarningRecommendation
              status="Rejected"
              type={income.type}
              rejectionReason={
                "assets" in income && !income.assets?.length
                  ? "No file was uploaded"
                  : ""
              }
              className="mb-4"
            />
          </Show>
          <OfferLetterAssets income={income} />
        </ContentTitle>
      )}

      {Object.keys(currentItemReverifyLog || {}).length ? (
        <div className="flex gap-4 items-center mb-2 mt-4 animate-appear">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="text-content-200 shrink-0"
            size={20}
          />

          <p className="text-sm font-medium text-content-400">
            Re-verification was initiated by{" "}
            <span className="font-bold text-content-1000">
              {currentItemReverifyLog?.author.name}
            </span>{" "}
            on{" "}
            <span className="font-bold text-content-1000">
              {moment(currentItemReverifyLog?.created_at).format(
                "ll [at] h:mm A"
              )}
            </span>
          </p>
        </div>
      ) : null}
      <Hide if={income.type === IncomeType.OfferLetter}>
        <div
          key={currentIncomeIndex}
          className={cx(
            "pb-4 animate-appear",
            groupedIncomeItems.length > 1 ? "pt-4" : ""
          )}
        >
          <VerifiedHeader
            applicationId={applicationId || 0}
            applicationStatus={applicationStatus}
            currentItem={currentItem}
            income={income}
            setShowReverifyIncomeModal={setShowReverifyIncomeModal}
            isManualApplicant={isManualApplicant}
          />

          <VerifiedReportedStats currentItem={currentItem} />

          {currentItem ? (
            <TablesSlice
              currentItem={currentItem}
              additionalData={additionalData}
            />
          ) : (
            <>
              <p className="text-base text-content-500">
                No verified income sources
              </p>
              <div className="h-[1px] w-full bg-alpha-40 my-3" />
              <div className="flex flex-wrap space-x-4">
                {income.assets?.map(({ asset, global_id }) => (
                  <AssetItem
                    key={global_id}
                    url={asset?.url || ""}
                    content_type={asset?.content_type || ""}
                    filename={asset?.filename || ""}
                    assetName={asset?.filename || ""}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </Hide>
    </>
  );
};
